import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, ViewEncapsulation, Input, Output, NgModule } from '@angular/core';

/**
 * Avatar represents people using icons, labels and images.
 * @group Components
 */
const _c0 = ["*"];
function Avatar_span_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 4);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.label);
  }
}
function Avatar_ng_template_3_span_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 6);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵclassMap(ctx_r0.icon);
    i0.ɵɵproperty("ngClass", "p-avatar-icon");
  }
}
function Avatar_ng_template_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, Avatar_ng_template_3_span_0_Template, 1, 3, "span", 5);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    const imageTemplate_r2 = i0.ɵɵreference(6);
    i0.ɵɵproperty("ngIf", ctx_r0.icon)("ngIfElse", imageTemplate_r2);
  }
}
function Avatar_ng_template_5_img_0_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "img", 8);
    i0.ɵɵlistener("error", function Avatar_ng_template_5_img_0_Template_img_error_0_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r0 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r0.imageError($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("src", ctx_r0.image, i0.ɵɵsanitizeUrl);
    i0.ɵɵattribute("aria-label", ctx_r0.ariaLabel);
  }
}
function Avatar_ng_template_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, Avatar_ng_template_5_img_0_Template, 1, 2, "img", 7);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngIf", ctx_r0.image);
  }
}
let Avatar = /*#__PURE__*/(() => {
  class Avatar {
    /**
     * Defines the text to display.
     * @group Props
     */
    label;
    /**
     * Defines the icon to display.
     * @group Props
     */
    icon;
    /**
     * Defines the image to display.
     * @group Props
     */
    image;
    /**
     * Size of the element.
     * @group Props
     */
    size = 'normal';
    /**
     * Shape of the element.
     * @group Props
     */
    shape = 'square';
    /**
     * Inline style of the element.
     * @group Props
     */
    style;
    /**
     * Class of the element.
     * @group Props
     */
    styleClass;
    /**
     * Establishes a string value that labels the component.
     * @group Props
     */
    ariaLabel;
    /**
     * Establishes relationships between the component and label(s) where its value should be one or more element IDs.
     * @group Props
     */
    ariaLabelledBy;
    /**
     * This event is triggered if an error occurs while loading an image file.
     * @param {Event} event - Browser event.
     * @group Emits
     */
    onImageError = new EventEmitter();
    /**
     * Holds the current style of the component to allow dynamic updates.
     */
    currentStyle = {};
    containerClass() {
      return {
        'p-avatar p-component': true,
        'p-avatar-image': this.image != null,
        'p-avatar-circle': this.shape === 'circle',
        'p-avatar-lg': this.size === 'large',
        'p-avatar-xl': this.size === 'xlarge'
      };
    }
    /**
     * Lifecycle hook to detect changes in inputs.
     */
    ngOnChanges(changes) {
      if (changes['style']) {
        this.currentStyle = {
          ...changes['style'].currentValue
        };
      }
    }
    imageError(event) {
      this.onImageError.emit(event);
    }
    static ɵfac = function Avatar_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || Avatar)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: Avatar,
      selectors: [["p-avatar"]],
      hostAttrs: [1, "p-element"],
      inputs: {
        label: "label",
        icon: "icon",
        image: "image",
        size: "size",
        shape: "shape",
        style: "style",
        styleClass: "styleClass",
        ariaLabel: "ariaLabel",
        ariaLabelledBy: "ariaLabelledBy"
      },
      outputs: {
        onImageError: "onImageError"
      },
      features: [i0.ɵɵNgOnChangesFeature],
      ngContentSelectors: _c0,
      decls: 7,
      vars: 9,
      consts: [["iconTemplate", ""], ["imageTemplate", ""], [3, "ngClass", "ngStyle"], ["class", "p-avatar-text", 4, "ngIf", "ngIfElse"], [1, "p-avatar-text"], [3, "class", "ngClass", 4, "ngIf", "ngIfElse"], [3, "ngClass"], [3, "src", "error", 4, "ngIf"], [3, "error", "src"]],
      template: function Avatar_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵelementStart(0, "div", 2);
          i0.ɵɵprojection(1);
          i0.ɵɵtemplate(2, Avatar_span_2_Template, 2, 1, "span", 3)(3, Avatar_ng_template_3_Template, 1, 2, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor)(5, Avatar_ng_template_5_Template, 1, 1, "ng-template", null, 1, i0.ɵɵtemplateRefExtractor);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          const iconTemplate_r4 = i0.ɵɵreference(4);
          i0.ɵɵclassMap(ctx.styleClass);
          i0.ɵɵproperty("ngClass", ctx.containerClass())("ngStyle", ctx.currentStyle);
          i0.ɵɵattribute("aria-labelledby", ctx.ariaLabelledBy)("aria-label", ctx.ariaLabel)("data-pc-name", "avatar");
          i0.ɵɵadvance(2);
          i0.ɵɵproperty("ngIf", ctx.label)("ngIfElse", iconTemplate_r4);
        }
      },
      dependencies: [i1.NgClass, i1.NgIf, i1.NgStyle],
      styles: ["@layer primeng{.p-avatar{display:inline-flex;align-items:center;justify-content:center;width:2rem;height:2rem;font-size:1rem}.p-avatar.p-avatar-image{background-color:transparent}.p-avatar.p-avatar-circle{border-radius:50%;overflow:hidden}.p-avatar .p-avatar-icon{font-size:1rem}.p-avatar img{width:100%;height:100%}}\n"],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return Avatar;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AvatarModule = /*#__PURE__*/(() => {
  class AvatarModule {
    static ɵfac = function AvatarModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || AvatarModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: AvatarModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule]
    });
  }
  return AvatarModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { Avatar, AvatarModule };
